/**
 * AdminLandingEditor — the platform homepage, section by section.
 * Toggle a section off and it vanishes from the public page; its wording is
 * kept for whenever it's switched back on.
 */
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import api, { getErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { uploadMedia } from '@/lib/mediaUpload';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowLeft, ArrowUp, ArrowDown, ExternalLink, Loader2, RotateCcw, Save,
  ChevronDown, ChevronUp, Lock, Plus, Trash2, Upload, Sun, Moon, Image, Columns,
} from 'lucide-react';
import { useSiteBrand } from '@/lib/useSiteBrand';

// Each palette carries its own accent — the switch flips both.
const PRESETS = {
  light: { surface: '#FAF7F1', accent: '#B4881F' },
  dark: { surface: '#0B0C0F', accent: '#D8B25A' },
};

const FIELDS = [
  ['kicker', 'Kicker'],
  ['heading', 'Heading'],
  ['blurb', 'Supporting line'],
  ['event_id', 'Event id or slug (blank = the next one coming up)'],
  ['cta_label', 'Button label'],
  ['cta_link', 'Button link'],
  ['secondary_label', 'Second button label'],
  ['secondary_link', 'Second button link'],
  ['bible_link', 'Bible tile link'],
  ['sermons_link', 'Sermons tile link'],
  ['worship_link', 'Worship tile link'],
  ['giving_link', 'Giving tile link'],
];

const SectionRow = ({ section, index, total, onChange, onMove }) => {
  const [open, setOpen] = useState(false);
  const set = (key) => (e) => onChange({
    ...section, props: { ...section.props, [key]: e.target.value },
  });
  const shown = FIELDS.filter(([k]) => k in (section.props || {}));

  return (
    <Card data-testid={`landing-section-${section.id}`}
          className={section.enabled ? '' : 'opacity-60'}>
      <CardContent className="py-4">
        <div className="flex items-center gap-4">
          <div className="flex flex-col gap-0.5">
            <button disabled={index === 0} onClick={() => onMove(index, -1)}
                    data-testid={`landing-move-up-${section.id}`}
                    className="text-slate-300 hover:text-slate-900 disabled:opacity-30">
              <ArrowUp className="h-3.5 w-3.5" />
            </button>
            <button disabled={index === total - 1} onClick={() => onMove(index, 1)}
                    data-testid={`landing-move-down-${section.id}`}
                    className="text-slate-300 hover:text-slate-900 disabled:opacity-30">
              <ArrowDown className="h-3.5 w-3.5" />
            </button>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-900">{section.label}</span>
              {section.locked && (
                <Badge variant="outline" className="text-[10px] gap-1">
                  <Lock className="h-2.5 w-2.5" /> always on
                </Badge>
              )}
              {!section.enabled && (
                <Badge variant="outline" className="text-[10px] bg-slate-100 text-slate-500">hidden</Badge>
              )}
            </div>
            {section.hint && <p className="text-xs text-slate-500 mt-0.5">{section.hint}</p>}
          </div>

          {section.locked ? (
            <span className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600"
                  data-testid={`landing-toggle-${section.id}`}>On</span>
          ) : (
            <Switch checked={section.enabled}
                    data-testid={`landing-toggle-${section.id}`}
                    onCheckedChange={(v) => onChange({ ...section, enabled: v })} />
          )}

          <Button variant="ghost" size="sm" data-testid={`landing-edit-${section.id}`}
                  onClick={() => setOpen((v) => !v)}>
            {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </div>

        {open && (
          <div className="mt-5 pt-5 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-4">
            {shown.map(([key, label]) => (
              <div key={key} className={key === 'blurb' ? 'md:col-span-2 space-y-1.5' : 'space-y-1.5'}>
                <Label className="text-xs">{label}</Label>
                <Input value={section.props[key] || ''} onChange={set(key)}
                       data-testid={`landing-field-${section.id}-${key}`} />
              </div>
            ))}
            {Array.isArray(section.props.points) && (
              <div className="md:col-span-2 space-y-2">
                <Label className="text-xs">Points</Label>
                {section.props.points.map((point, i) => (
                  <div key={i} className="flex gap-2">
                    <Input value={point} data-testid={`landing-point-${section.id}-${i}`}
                           onChange={(e) => onChange({
                             ...section,
                             props: {
                               ...section.props,
                               points: section.props.points.map((x, j) => (j === i ? e.target.value : x)),
                             },
                           })} />
                    <Button variant="ghost" size="icon"
                            data-testid={`landing-point-remove-${section.id}-${i}`}
                            onClick={() => onChange({
                              ...section,
                              props: {
                                ...section.props,
                                points: section.props.points.filter((_, j) => j !== i),
                              },
                            })}>
                      <Trash2 className="h-4 w-4 text-slate-400" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" data-testid={`landing-point-add-${section.id}`}
                        onClick={() => onChange({
                          ...section,
                          props: { ...section.props, points: [...section.props.points, ''] },
                        })}>
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> Add point
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const HeroMediaCard = ({ section, onChange }) => {
  const [busy, setBusy] = useState(null);
  const props = section?.props || {};

  const upload = async (file, key) => {
    if (!file) return;
    setBusy(key);
    try {
      const asset = await uploadMedia(file, {
        purpose: key === 'hero_video' ? 'video' : 'hero',
      });
      onChange({ ...section, props: { ...props, [key]: asset.url } });
      toast.success(`${asset.filename} uploaded — press Save to publish it`);
    } catch (e) {
      toast.error(e.message || 'That file could not be uploaded');
    } finally {
      setBusy(null);
    }
  };

  const set = (key, value) => onChange({ ...section, props: { ...props, [key]: value } });

  return (
    <Card data-testid="landing-hero-media-card">
      <CardContent className="py-5 space-y-5">
        <div>
          <h2 className="font-semibold text-slate-900 flex items-center gap-2">
            <Image className="h-4 w-4 text-slate-400" /> Hero picture or video
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            The big visual beside your headline. A video plays muted on a loop and uses the
            picture as its still frame.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[['hero_image', 'Picture', 'image/*'], ['hero_video', 'Video', 'video/*']].map(
            ([key, label, accept]) => (
              <div key={key} className="space-y-3">
                <Label className="text-xs">{label}</Label>
                {props[key] ? (
                  key === 'hero_video' ? (
                    <video src={props[key]} muted controls
                           data-testid="landing-hero-video-preview"
                           className="h-28 w-full object-cover border border-slate-200 bg-black" />
                  ) : (
                    <img src={props[key]} alt="Hero" data-testid="landing-hero-preview"
                         className="h-28 w-full object-cover border border-slate-200" />
                  )
                ) : (
                  <div className="h-28 w-full grid place-items-center border border-dashed
                                  border-slate-300 text-[11px] text-slate-400">
                    Nothing yet
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <label className="inline-flex">
                    <input type="file" accept={accept} className="hidden"
                           data-testid={`landing-hero-upload-${key === 'hero_video' ? 'video' : 'hero'}`}
                           onChange={(e) => upload(e.target.files?.[0], key)} />
                    <Button asChild variant="outline" size="sm" disabled={busy === key}>
                      <span>
                        {busy === key ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                      : <Upload className="h-3.5 w-3.5 mr-1.5" />}
                        {props[key] ? 'Replace' : `Upload a ${label.toLowerCase()}`}
                      </span>
                    </Button>
                  </label>
                  {props[key] && (
                    <Button variant="ghost" size="sm" onClick={() => set(key, '')}
                            data-testid={`landing-hero-clear-${key}`}>Remove</Button>
                  )}
                </div>
              </div>
            ))}
        </div>
      </CardContent>
    </Card>
  );
};

const ComparePreview = ({ open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-[96vw] w-[96vw]" data-testid="landing-compare-dialog">
      <DialogHeader>
        <DialogTitle>Light and dark, side by side</DialogTitle>
        <DialogDescription>
          Both are live previews of your saved homepage — pick the palette above and save
          to publish it.
        </DialogDescription>
      </DialogHeader>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[['light', 'Light'], ['dark', 'Dark']].map(([mode, label]) => (
          <div key={mode} className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
            <iframe src={`/?mode=${mode}`} title={`${label} preview`}
                    data-testid={`landing-preview-${mode}`}
                    className="w-full h-[62vh] border border-slate-200 bg-white" />
          </div>
        ))}
      </div>
    </DialogContent>
  </Dialog>
);

const AdminLandingEditor = () => {
  const queryClient = useQueryClient();
  const [sections, setSections] = useState(null);
  const [accent, setAccent] = useState('');
  const [mode, setMode] = useState('light');
  const [serif, setSerif] = useState(true);
  const [compare, setCompare] = useState(false);
  const { accent: brandAccent } = useSiteBrand();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-landing'],
    queryFn: async () => (await api.get('/admin/landing')).data,
    retry: false,
  });

  useEffect(() => {
    if (data && !sections) {
      setSections(data.sections);
      setAccent(data.theme?.accent || PRESETS.light.accent);
      setMode(data.theme?.mode === 'dark' ? 'dark' : 'light');
      setSerif(data.theme?.serif_headings !== false);
    }
  }, [data, sections]);

  const save = useMutation({
    mutationFn: async () => (await api.put('/admin/landing', {
      sections: sections.map((s, i) => ({
        id: s.id, enabled: s.enabled, order: i, props: s.props,
      })),
      theme: { accent, mode, surface: PRESETS[mode].surface, serif_headings: serif },
    })).data,
    onSuccess: (res) => {
      toast.success('Homepage updated');
      setSections(res.sections);
      queryClient.invalidateQueries({ queryKey: ['public-landing'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const reset = useMutation({
    mutationFn: async () => (await api.post('/admin/landing/reset')).data,
    onSuccess: (res) => {
      toast.success('Back to the defaults');
      setSections(res.sections);
      setAccent(res.theme?.accent || PRESETS.light.accent);
      setMode(res.theme?.mode === 'dark' ? 'dark' : 'light');
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (error) {
    return (
      <div className="p-10 text-center" data-testid="landing-editor-forbidden">
        <p className="text-slate-600">Only platform super admins can edit the homepage.</p>
      </div>
    );
  }

  if (isLoading || !sections) {
    return <div className="p-10"><Skeleton className="h-64 w-full" /></div>;
  }

  const move = (index, delta) => {
    const next = [...sections];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setSections(next);
  };

  const liveCount = sections.filter((s) => s.enabled).length;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-landing-editor">
      <div className="mx-auto max-w-4xl px-6 py-10 space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <Link to="/admin" className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1.5">
              <ArrowLeft className="h-3.5 w-3.5" /> Admin
            </Link>
            <h1 className="mt-2 text-2xl font-bold text-slate-900">Homepage</h1>
            <p className="text-sm text-slate-500 mt-1">
              {liveCount} of {sections.length} sections showing. Switching one off keeps its wording.
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/landing-editor/legacy">
              <Button variant="ghost" data-testid="landing-legacy-btn">Old settings</Button>
            </Link>
            <Button variant="outline" onClick={() => setCompare(true)}
                    data-testid="landing-compare-btn">
              <Columns className="h-3.5 w-3.5 mr-1.5" /> Compare light & dark
            </Button>
            <a href="/" target="_blank" rel="noreferrer">
              <Button variant="outline" data-testid="landing-preview-btn">
                View page <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </a>
            <Button onClick={() => save.mutate()} disabled={save.isPending}
                    data-testid="landing-save-btn">
              {save.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              : <Save className="h-4 w-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="py-5 flex flex-wrap items-end gap-6">
            <div className="space-y-1.5">
              <Label className="text-xs">Palette</Label>
              <div className="inline-flex border border-slate-200 rounded-md overflow-hidden">
                {[['light', 'Light', Sun], ['dark', 'Dark', Moon]].map(([key, label, Icon]) => (
                  <button key={key} data-testid={`landing-mode-${key}`}
                          onClick={() => { setMode(key); setAccent(PRESETS[key].accent); }}
                          className={`px-4 h-9 text-sm font-medium inline-flex items-center gap-2
                            ${mode === key ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Accent colour</Label>
              <div className="flex items-center gap-2">
                <span className="h-9 w-9 rounded-full border border-slate-200"
                      style={{ backgroundColor: brandAccent || accent }}
                      data-testid="landing-accent-swatch" />
                <a href="/admin/heroes" className="text-xs text-slate-600 underline"
                   data-testid="landing-accent-link">
                  Set in Site Appearance → Brand
                </a>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <Switch checked={serif} onCheckedChange={setSerif} data-testid="landing-serif-toggle" />
              Editorial serif headings
            </label>
            <Button variant="ghost" size="sm" className="ml-auto" data-testid="landing-reset-btn"
                    onClick={() => reset.mutate()} disabled={reset.isPending}>
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Restore defaults
            </Button>
          </CardContent>
        </Card>

        <HeroMediaCard section={sections.find((s) => s.id === 'hero')}
                       onChange={(next) => setSections(sections.map(
                         (x) => (x.id === 'hero' ? next : x)))} />

        <div className="space-y-3">
          {sections.map((s, i) => (
            <SectionRow key={s.id} section={s} index={i} total={sections.length}
                        onMove={move}
                        onChange={(next) => setSections(sections.map((x, j) => (j === i ? next : x)))} />
          ))}
        </div>
      </div>
      <ComparePreview open={compare} onOpenChange={setCompare} />
    </div>
  );
};

export default AdminLandingEditor;
